import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { dataService } from "../../../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { Download, Plus, Search } from "lucide-react";
import { downloadCsv } from "../../../../../Utils/csvExport";
import Button from "../../../../Shared/Buttons/Button";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../../../Shared/Select";
import ResetFiltersButton from "../../../../Filter/ResetFiltersButton";
import { crudTypes, AccountFilterTypes } from "../../../../../Config/constants";
import TablePagination from "@mui/material/TablePagination";
import useFilterPersistence from "../../../../../hooks/useFilterPersistence";
// Table
import AccountCategoryTable from "../Account/AccountCategoryTable";
// Dialogs (shared with AccountOverview)
import CreateAccountCategoryDialog from "../../../AccountOverview/Components/CreateAccountCategoryDialog";
import DeleteConfirmDialog from "../../../AccountOverview/Components/DeleteConfirmDialog";

function AccountCategoryTab(props) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [originalCategories, setOriginalCategories] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [companies, setCompanies] = useState([]);

  const { filters, setFilter, setFilterText, resetFilters, getFilterValue, hasActiveFilters, page, setPage, pageSize, setPageSize } = useFilterPersistence({
    pageKey: "financial-account-categories",
    filterSchema: { acccat_search: AccountFilterTypes.SEARCH, acccat_company: AccountFilterTypes.COMPANY },
    defaultPageSize: 25,
    persistPageSize: true,
  });

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, target: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const flattenCategories = (companiesList) => {
    const rows = [];
    (companiesList || []).forEach((company) => {
      (company.accountCategories || []).forEach((category) => {
        rows.push({
          id: category.id,
          name: category.name,
          isRevenue: category.isRevenue,
          companyId: company.id,
          companyName: company.name,
        });
      });
    });
    return rows;
  };

  useEffect(() => {
    dataService
      .getAllUserCompanyAccountCategories(props.Login.userid, 0, 0)
      .then((response) => {
        setCompanies(response || []);
        setOriginalCategories(flattenCategories(response));
      })
      .catch((error) => {
        console.error("Error fetching account categories:", error);
      });
  }, [props.Login.userid]);

  const filterList = (thisFilters) => {
    let filtered = [...originalCategories];
    for (let i = 0; i < thisFilters.length; i++) {
      const f = thisFilters[i];
      switch (f.type) {
        case AccountFilterTypes.SEARCH:
          filtered = filtered.filter((x) => x.name.toLowerCase().includes(f.filterValue.toLowerCase()));
          break;
        case AccountFilterTypes.COMPANY:
          filtered = filtered.filter((x) => x.companyId === parseInt(f.filterValue) || f.filterValue === "");
          break;
        default:
          filtered = filtered.filter((x) => x.name.toLowerCase().includes(f.filterValue.toLowerCase()));
          break;
      }
    }
    filtered = filtered.sort((a, b) => a.name.localeCompare(b.name));
    setCategoryList(filtered);
  };

  useEffect(() => {
    filterList(filters);
  }, [originalCategories, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Dialog open/close ----
  const openCreateDialog = () => setCreateDialogOpen(true);

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setCategoryToEdit(null);
  };

  const handleEditCategory = (categoryId) => {
    const category = originalCategories.find((x) => x.id === categoryId);
    if (!category) return;
    setCategoryToEdit({
      id: category.id,
      name: category.name,
      companyId: category.companyId,
      isRevenue: category.isRevenue,
      accounts: [],
    });
  };

  // ---- Delete ----
  const handleDeleteCategory = (categoryId) => {
    const category = originalCategories.find((x) => x.id === categoryId);
    if (!category) return;
    setDeleteDialog({ open: true, target: category });
    setDeleteError(null);
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, target: null });
    setDeleteError(null);
  };

  const handleConfirmDelete = () => {
    const category = deleteDialog.target;
    if (!category) return;
    setDeleteLoading(true);
    dataService.deleteAccountCategory(category.id, props.Login.userid).then((response) => {
      setDeleteLoading(false);
      if (response && response.success) {
        toast.success(response.message);
        setOriginalCategories((prev) => prev.filter((x) => x.id !== category.id));
        setCompanies((prev) =>
          prev.map((c) =>
            c.id !== category.companyId
              ? c
              : { ...c, accountCategories: (c.accountCategories || []).filter((ac) => ac.id !== category.id) }
          )
        );
        closeDeleteDialog();
      } else {
        setDeleteError(response ? response.message : t('accountCategoryTab.deleteError'));
      }
    });
  };

  // ---- CRUD success handlers ----
  const handleCategoryCrudSuccess = (category, crudType) => {
    switch (crudType) {
      case crudTypes.CREATE:
        const newRow = {
          id: category.id,
          name: category.name,
          isRevenue: category.isRevenue,
          companyId: category.companyId,
          companyName: companies.find((c) => c.id === category.companyId)?.name || "",
        };
        setOriginalCategories((prev) => [...prev, newRow]);
        setCompanies((prev) =>
          prev.map((c) =>
            c.id !== category.companyId
              ? c
              : {
                  ...c,
                  accountCategories: [...(c.accountCategories || []), { ...category }].sort((a, b) => a.name.localeCompare(b.name)),
                }
          )
        );
        break;
      case crudTypes.UPDATE:
        setOriginalCategories((prev) =>
          prev.map((x) => (x.id !== category.id ? x : { ...x, name: category.name, isRevenue: category.isRevenue }))
        );
        setCompanies((prev) =>
          prev.map((c) =>
            c.id !== category.companyId
              ? c
              : {
                  ...c,
                  accountCategories: (c.accountCategories || []).map((ac) =>
                    ac.id === category.id ? { ...ac, name: category.name, isRevenue: category.isRevenue } : ac
                  ),
                }
          )
        );
        break;
      default:
        break;
    }
  };

  // ---- Filter helpers ----
  const getSelectedCompany = () => {
    const companyId = getFilterValue(AccountFilterTypes.COMPANY);
    return companies.find((c) => c.id === parseInt(companyId)) || null;
  };

  const handleExport = () => {
    const headers = [t('common.name'), t('user.company'), t('common.type')];
    const rows = categoryList.map((cat) => [
      cat.name,
      cat.companyName,
      cat.isRevenue ? t('accountCategoryTable.revenue') : t('accountCategoryTable.expense'),
    ]);
    downloadCsv('account-categories.csv', headers, rows);
  };

  const paginatedCategories = categoryList.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <React.Fragment>
      <div className={classes.tableManagementContainer}>
        <div className={classes.tableManagementContainer__start}>
          <div className={classes.searchContainer}>
            <input
              placeholder={t('accountCategoryTab.searchByName')}
              className={classes.search__input}
              type="text"
              value={getFilterValue(AccountFilterTypes.SEARCH)}
              onChange={(e) => setFilterText(AccountFilterTypes.SEARCH, e.target.value)}
            />
            <Search size={16} className={classes.inputIcon} />
          </div>
          {companies.length > 1 && (
            <div className={classes.filterContainer}>
              <Select value={getSelectedCompany()?.id} onChange={(company) => setFilter(AccountFilterTypes.COMPANY, company?.id ?? "")} size="small">
                <SelectTrigger>{getSelectedCompany()?.name || t('accountCategoryTab.allCompanies')}</SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>{t('accountCategoryTab.allCompanies')}</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
        </div>
        <div className={classes.tableManagementContainer__end}>
          <Button size="small" variant="secondary" startIcon={<Download />} onClick={handleExport}>
            {t('common.export')}
          </Button>
          <Button size="small" startIcon={<Plus />} onClick={openCreateDialog}>
            {t('accountCategoryTab.createAccountCategory')}
          </Button>
        </div>
      </div>

      <AccountCategoryTable
        categories={paginatedCategories}
        handleEditCategory={handleEditCategory}
        handleDeleteCategory={handleDeleteCategory}
        searchValue={getFilterValue(AccountFilterTypes.SEARCH)}
      />

      <TablePagination
        component="div"
        count={categoryList.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => setPageSize(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      <CreateAccountCategoryDialog
        dialogOpen={createDialogOpen || !!categoryToEdit}
        closeDialog={closeCreateDialog}
        accountCategoryToEdit={categoryToEdit}
        companies={companies}
        userId={props.Login.userid}
        onSuccess={handleCategoryCrudSuccess}
      />

      <DeleteConfirmDialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        error={deleteError}
        title={t('accountCategoryTab.deleteAccountCategory')}
        item={deleteDialog.target?.name}
        consequences={
          deleteDialog.target
            ? [
                t('accountCategoryTab.deactivateCategory', { name: deleteDialog.target.name }),
                t('accountCategoryTab.deactivateAllAccounts'),
              ]
            : []
        }
      />
    </React.Fragment>
  );
}

export default AccountCategoryTab;

const useStyles = makeStyles({
  tableManagementContainer: {
    width: "100%",
    height: "fit-content",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap-reverse",

    marginBottom: "8px",
  },

  tableManagementContainer__start: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },

  searchContainer: {
    width: "240px",
    position: "relative",
  },

  filterContainer: {
    width: "auto",
    minWidth: "fit-content",
  },

  search__input: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    paddingRight: "32px",

    borderRadius: "4px",
    border: "1px solid #DCDCDC",

    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",

    backgroundColor: "#FFFFFF",

    "&:focus": {
      outline: "none",
      border: "1px solid var(--color-primary)",
    },
  },

  inputIcon: {
    color: "#0C0E29",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: "8px",
  },

  tableManagementContainer__end: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },
});
